<template>
  <div class="member">
    <TeamMemberAvatar
      :id="member.id"
      class="member__avatar"
      :size="35"
    />
    <div class="member__name-section">
      <div class="member__name">
        {{ membershipStore.memberNameById[member.id] }}
      </div>
      <div
        v-if="member.email"
        class="member__email"
      >
        {{ member.email }}
      </div>
    </div>
    <div class="member__role">
      <RoleDropdown
        :disabled="!canUpdateMembership"
        name="membership-role"
        :value="member.role"
        :options="canUpdateMembership ? roleOptions : ROLE_OPTIONS"
        @input="onUpdate(member, $event)"
      />
    </div>
    <div class="member__menu">
      <ItemMenu
        v-if="canDeleteMembership"
        @delete="onDelete(member)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import TeamMemberAvatar from '@/modules/Teams/TeamMemberAvatar.vue'
import RoleDropdown from '@/modules/Teams/RoleDropdown.vue'
import { useAuth } from '@/modules/Auth/useAuth'
import type { MembershipPayload } from '@/store/types'

import { ROLE_OPTIONS, getMembershipRoleOptions } from '@/modules/Teams/roleData'
import ItemMenu from './ItemMenu.vue'
import { useMembershipStore } from '@/modules/Teams/useMembershipStore'

const props = defineProps<{
  member: MembershipPayload
}>()

const emit = defineEmits<{
  (
    e: 'update',
    {
      membership,
      newRole,
    }: {
      membership: MembershipPayload
      newRole: MembershipPayload['role']
    },
  ): void
  (e: 'delete', membership: MembershipPayload): void
}>()

const { isAuthorized } = useAuth()
const membershipStore = useMembershipStore()

const roleOptions = computed(() => getMembershipRoleOptions(isAuthorized))

const canUpdateMembership = computed(() =>
  isAuthorized('update_membership', { subject: 'membership', resource: props.member }),
)

const onUpdate = (membership: MembershipPayload, newRole: MembershipPayload['role']): void =>
  emit('update', { membership, newRole })

const canDeleteMembership = computed(() =>
  isAuthorized('delete_membership', { subject: 'membership', resource: props.member }),
)

const onDelete = (membership: MembershipPayload): void => emit('delete', membership)
</script>

<style lang="scss" scoped>
@import './MemberBase.scss';
</style>
